import type { ListItemButtonProps } from '@mui/material';
import { List, ListItemButton, ListItemText, Paper } from '@mui/material';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';

import * as ROUTES from '@constants/routes';

import { useSession } from '@hooks/useSession';

import type { Profile } from '@models/session';

import { isUser } from '@utils/role';

interface ProfileAndSecurityProps {
  user?: Profile;
}

const messages = defineMessages({
  account: {
    id: 'updateProfile.account',
    defaultMessage: 'Account',
  },
  changePassword: {
    id: 'updateProfile.changePassword',
    defaultMessage: 'Change Password',
  },
  accountHolder: {
    id: 'updateProfile.accountHolder',
    defaultMessage: 'Account holder',
  },
  updateProfile: {
    id: 'updateProfile.updateProfile',
    defaultMessage: 'Update Profile',
  },
  changeEmail: {
    id: 'updateProfile.changeEmail',
    defaultMessage: 'Change Email',
  },
  changeName: {
    id: 'updateProfile.changeName',
    defaultMessage: 'Change Name',
  },
  changeAvatar: {
    id: 'updateProfile.changeAvatar',
    defaultMessage: 'Change Profile Picture',
  },
  mfa: {
    id: 'updateProfile.changeMfa',
    defaultMessage: 'Change Multi-Factor Authentication (MFA)',
  },
  changePhone: {
    id: 'updateProfile.changePhone',
    defaultMessage: 'Change Phone Number',
  },
});

const ProfileAndSecurity: React.FC<ProfileAndSecurityProps> = ({ user }) => {
  const { formatMessage } = useIntl();
  const {
    state: { loggedInProfile },
  } = useSession();

  if (!user) {
    return null;
  }

  const isSelf = loggedInProfile?.user_id === user.user_id;

  const listItemButtonProps: ListItemButtonProps = {
    divider: true,
  };

  const secondaryAction = (
    <Icon color="text.primary" name="chevron-down" rotation={-90} type="fas" />
  );

  return (
    <Paper>
      <List>
        <ListItemButton
          {...listItemButtonProps}
          onClick={() => navigate(ROUTES.UPDATE_NAME)}
        >
          <ListItemText primary={formatMessage(messages.changeName)} />
          {secondaryAction}
        </ListItemButton>

        {isUser(user) ? (
          <ListItemButton
            {...listItemButtonProps}
            onClick={() => navigate(ROUTES.UPDATE_EMAIL)}
          >
            <ListItemText primary={formatMessage(messages.changeEmail)} />
            {secondaryAction}
          </ListItemButton>
        ) : null}

        {isUser(user) && isSelf ? (
          <ListItemButton
            {...listItemButtonProps}
            onClick={() => navigate(ROUTES.UPDATE_PASSWORD)}
          >
            <ListItemText primary={formatMessage(messages.changePassword)} />
            {secondaryAction}
          </ListItemButton>
        ) : null}

        <ListItemButton
          {...listItemButtonProps}
          onClick={() => navigate(ROUTES.UPDATE_AVATAR)}
        >
          <ListItemText primary={formatMessage(messages.changeAvatar)} />
          {secondaryAction}
        </ListItemButton>

        {isUser(user) && (
          <ListItemButton
            {...listItemButtonProps}
            onClick={() => navigate(ROUTES.UPDATE_PHONE)}
          >
            <ListItemText primary={formatMessage(messages.changePhone)} />
            {secondaryAction}
          </ListItemButton>
        )}

        {/** Need to be Cognito user to (en/dis)able MFA */}
        {isUser(user) && (
          <ListItemButton
            {...listItemButtonProps}
            onClick={() => navigate(ROUTES.UPDATE_MFA)}
          >
            <ListItemText primary={formatMessage(messages.mfa)} />
            {secondaryAction}
          </ListItemButton>
        )}
      </List>
    </Paper>
  );
};

export default ProfileAndSecurity;
